import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody } from './BaseComponents';
import { QuizRecoveryNotificationProps } from '../types';

/**
 * QuizRecoveryNotification Component
 * 
 * Shows a notification when a quiz session can be recovered from localStorage backup.
 * Appears when users return to a quiz after a browser refresh, network interruption, or crash.
 */
export const QuizRecoveryNotification: React.FC<QuizRecoveryNotificationProps> = ({
  attemptId,
  onRecover,
  onDismiss,
  className = ''
}) => {
  const [backupData, setBackupData] = useState<any>(null);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    // Check for backup data
    const checkForBackup = () => {
      try {
        const backupKey = `quiz_backup_${attemptId}`;
        const backup = localStorage.getItem(backupKey);
        
        if (backup) {
          const parsedBackup = JSON.parse(backup);
          const backupAge = Date.now() - parsedBackup.timestamp;
          
          // Only show recovery for backups less than 24 hours old
          if (backupAge < 24 * 60 * 60 * 1000) {
            setBackupData(parsedBackup);
            setShowNotification(true);
          }
        }
      } catch (error) {
        console.warn('Failed to check for quiz backup:', error);
      }
    };

    checkForBackup();
  }, [attemptId]);

  const handleRecover = () => {
    onRecover();
    setShowNotification(false);
  };

  const handleDismiss = () => {
    onDismiss();
    setShowNotification(false);
    
    // Clear the backup since user chose not to recover
    try {
      const backupKey = `quiz_backup_${attemptId}`;
      localStorage.removeItem(backupKey);
    } catch (error) {
      console.warn('Failed to clear backup:', error);
    }
  };

  if (!showNotification || !backupData) {
    return null;
  }

  const formatTimeAgo = (timestamp: number) => {
    const diffInMs = Date.now() - timestamp;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    
    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  };

  return (
    <Card className={`border-2 border-orange-200 bg-orange-50 ${className}`}>
      <CardBody>
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="icon-circle-sm bg-orange-100">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-orange-900 mb-2">
              Resume Previous Session?
            </h3>
            
            <p className="text-orange-800 mb-3">
              We found a saved session for this quiz from {formatTimeAgo(backupData.timestamp)}.
              You can continue where you left off.
            </p>
            
            <div className="bg-white rounded-lg p-3 mb-4 border border-orange-200">
              <div className="text-sm text-gray-700">
                <div className="flex justify-between items-center mb-1">
                  <span>Progress:</span>
                  <span className="font-medium">
                    Question {(backupData.currentQuestionIndex || 0) + 1} of {backupData.totalQuestions}
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full progress-bar-dynamic"
                    style={{ 
                      width: `${((backupData.currentQuestionIndex || 0) + 1) / backupData.totalQuestions * 100}%` 
                    }}
                  />
                </div>
                
                <div className="text-xs text-gray-600">
                  {backupData.answers?.filter((a: any) => a.userAnswer)?.length || 0} answers saved locally
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="primary"
                onClick={handleRecover}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Resume Quiz
              </Button>
              
              <Button
                variant="secondary"
                onClick={handleDismiss}
                className="text-gray-600 hover:text-gray-800"
              >
                Start Fresh
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default QuizRecoveryNotification;